import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckSquare, Users, Clock, Plus, CalendarIcon, Loader2, Trash2, Globe } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function AdminTodos() {
  const { api } = useAuth();
  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  
  const [form, setForm] = useState({
    user_id: "",
    title: "",
    description: "",
    due_date: null,
  });

  const fetchData = async () => {
    try {
      const [todosRes, usersRes] = await Promise.all([
        api().get("/admin/todos", {
          params: selectedUser !== "all" ? { user_id: selectedUser } : {}
        }),
        api().get("/admin/users"),
      ]);
      setTodos(todosRes.data);
      setUsers(usersRes.data.filter(u => u.role === "staff"));
    } catch (error) {
      toast.error("Failed to load todos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedUser]);

  const resetForm = () => {
    setForm({
      user_id: "",
      title: "",
      description: "",
      due_date: null,
    });
  };

  const handleAssignTask = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.title) {
      toast.error("Please select a staff member and enter a task title");
      return;
    }
    
    setSaving(true);
    try {
      await api().post("/admin/todos/assign", {
        user_id: form.user_id,
        title: form.title,
        description: form.description,
        due_date: form.due_date ? format(form.due_date, "yyyy-MM-dd") : null,
      });
      toast.success("Task assigned successfully! Email notification sent.");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign task");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      await api().delete(`/admin/todos/${todoId}`);
      toast.success("Task deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const pendingTodos = todos.filter(t => !t.is_completed);
  const completedTodos = todos.filter(t => t.is_completed);

  // Group by user
  const todosByUser = todos.reduce((acc, t) => {
    const name = t.user_name || "Unknown";
    if (!acc[name]) acc[name] = { pending: 0, completed: 0 };
    if (t.is_completed) acc[name].completed++;
    else acc[name].pending++;
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="admin-todos">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Staff Tasks</h1>
          <p className="text-slate-500 mt-1">View all staff tasks and assign new tasks</p>
        </div>
        
        <div className="flex gap-3">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="assign-task-btn">
                <Plus className="mr-2 h-4 w-4" />
                Assign Task
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Assign Task to Staff</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAssignTask} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Staff Member *</Label>
                  <Select
                    value={form.user_id}
                    onValueChange={(value) => setForm({ ...form, user_id: value })}
                  >
                    <SelectTrigger data-testid="assign-staff-select">
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Task Title *</Label>
                  <Input
                    id="title"
                    placeholder="What needs to be done?"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="assign-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Add more details..."
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    data-testid="assign-description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Due Date (optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="assign-due-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.due_date ? format(form.due_date, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.due_date}
                        onSelect={(date) => setForm({ ...form, due_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={saving}
                  data-testid="assign-submit-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Assigning...
                    </>
                  ) : "Assign Task"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          
          <div className="w-48">
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger data-testid="filter-staff-select">
                <SelectValue placeholder="Filter by staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{pendingTodos.length}</p>
                <p className="text-sm text-slate-500">Pending Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <CheckSquare className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{completedTodos.length}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{Object.keys(todosByUser).length}</p>
                <p className="text-sm text-slate-500">Staff with Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* By Staff Summary */}
      {Object.keys(todosByUser).length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Tasks by Staff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(todosByUser).map(([name, data]) => (
                <div key={name} className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900">{name}</p>
                  <div className="flex justify-between mt-2 text-sm">
                    <span className="text-amber-600">{data.pending} pending</span>
                    <span className="text-emerald-600">{data.completed} done</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Todos Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">All Tasks</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>Task</th>
                  <th>Due Date</th>
                  <th>Assigned By</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {todos.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      No tasks found
                    </td>
                  </tr>
                ) : (
                  todos.map((todo) => (
                    <tr key={todo.id} data-testid={`todo-row-${todo.id}`}>
                      <td className="font-medium">{todo.user_name}</td>
                      <td>
                        <div>
                          <p className={todo.is_completed ? "line-through text-slate-400" : ""}>{todo.title}</p>
                          {todo.description && (
                            <p className="text-xs text-slate-500 mt-1 truncate max-w-[200px]">{todo.description}</p>
                          )}
                        </div>
                      </td>
                      <td className="text-sm">
                        {todo.due_date ? formatTableDateSydney(todo.due_date) : "-"}
                      </td>
                      <td className="text-sm text-slate-500">
                        {todo.assigned_by_name || "Self"}
                      </td>
                      <td>
                        <Badge className={todo.is_completed ? "badge-approved" : "badge-pending"}>
                          {todo.is_completed ? "Done" : "Pending"}
                        </Badge>
                      </td>
                      <td className="text-sm text-slate-500">
                        {formatSydney(todo.created_at, "MMM dd")}
                      </td>
                      <td>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`delete-todo-${todo.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Task?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the task &quot;{todo.title}&quot; for {todo.user_name}. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteTodo(todo.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
